import { OrderFilterOptions } from 'const/OrderFilterOptions';
import { createGroupColors } from 'pages/oldPlanner/planner-map/map/PlannerMapMapUtils';
import { useMemo } from 'react';
import { BundleJson } from 'types/bundleTypes';
import { FilterType } from '../Planner';
import {
  createFeatureStructure,
  PlannerOrder,
  uniqueWindowGroups,
} from '../utils/usePlannerData';

export const usePlannerFilteredData = (
  filters: FilterType,
  defaultBundles: BundleJson[],
  defaultOrders: PlannerOrder[],
  scans?: Array<boolean>
) => {
  const activeFilters = useMemo(() => {
    const filtersObject = Object.keys(filters);

    /* Get only active Filters */
    const newActiveFilters = filtersObject.filter(activeFilter => {
      const value = filters[activeFilter];
      const isArrayFilterOption = Array.isArray(value);
      if (isArrayFilterOption) {
        return value.length > 0;
      }
      return !!value;
    });

    return newActiveFilters;
  }, [filters]);

  const fetchedBundles: BundleJson[] = useMemo(() => {
    if (activeFilters?.length <= 0) {
      return undefined;
    }

    /* Filter the Bundles */
    let filteredBundles = [...defaultBundles];
    activeFilters.forEach(activeFilter => {
      filteredBundles = filteredBundles.filter(bundle => {
        let shouldBeFiltered = false;
        switch (activeFilter) {
          case 'windows':
            shouldBeFiltered = true;
            break;
          case 'stores':
            shouldBeFiltered = true;
            break;
          case 'productType':
            shouldBeFiltered = true;
            break;
          case OrderFilterOptions.WITH_TEMP_CONTROL:
            shouldBeFiltered = true;
            break;
          case OrderFilterOptions.WITHOUT_TEMP_CONTROL:
            shouldBeFiltered = true;
            break;
          case OrderFilterOptions.WITH_ID_CHECK:
            shouldBeFiltered = true;
            break;
          case OrderFilterOptions.RESCHEDULED:
            shouldBeFiltered = true;
            break;
          case 'bundleStatus':
            shouldBeFiltered = filters[activeFilter].includes(bundle.status);
            break;
          default:
            break;
        }
        return shouldBeFiltered;
      });
    });

    return filteredBundles;
  }, [activeFilters, defaultBundles, filters]);

  const fetchedOrders: PlannerOrder[] = useMemo(() => {
    /* Filter Orders */
    const filterredOrderReducer = (
      accumulator: Array<PlannerOrder>,
      currentOrder: PlannerOrder
    ) => {
      const existingAccumulator = accumulator;
      let passedFilter = true;

      const orderWithHubScan = scans?.[currentOrder.id];

      /* Do Filters */
      activeFilters.forEach(filter => {
        switch (filter) {
          case 'windows':
            if (!filters[filter]?.includes(currentOrder.window)) {
              passedFilter = false;
            }
            break;
          case 'stores':
            if (!filters[filter]?.includes(currentOrder.senderName)) {
              passedFilter = false;
            }
            break;
          case 'productType':
            if (currentOrder.product !== filters[filter]) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.WITH_TEMP_CONTROL:
            if (!currentOrder.temperatureControlled) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.WITHOUT_TEMP_CONTROL:
            if (currentOrder.temperatureControlled) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.WITH_ID_CHECK:
            if (!currentOrder.idCheck) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.XXL:
            if (
              !(
                currentOrder.xxl ||
                defaultOrders.some(other => {
                  return (
                    other.xxl && currentOrder.deliveryId === other.deliveryId
                  );
                })
              )
            ) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.WITH_PARTNER_SCAN:
            if (!currentOrder.scannedAtPartner) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.WITHOUT_PARTNER_SCAN:
            if (!!currentOrder.scannedAtPartner) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.WITH_HUB_SCAN:
            if (!orderWithHubScan) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.WITHOUT_HUB_SCAN:
            if (orderWithHubScan) {
              passedFilter = false;
            }
            break;
          case OrderFilterOptions.RESCHEDULED:
            if (!(currentOrder.timeWasChanged && !!currentOrder.scannedAtHub)) {
              passedFilter = false;
            }
            break;
          default:
            break;
        }
      });

      if (passedFilter) {
        existingAccumulator.push(currentOrder);
      }

      return existingAccumulator;
    };

    const filteredOrder = defaultOrders.reduce<Array<PlannerOrder>>(
      filterredOrderReducer,
      []
    );

    return filteredOrder;
  }, [activeFilters, defaultOrders, filters, scans]);

  const featureStructure = useMemo(
    () =>
      !!fetchedOrders && !!fetchedBundles
        ? createFeatureStructure(fetchedOrders, fetchedBundles)
        : {
            ordersFeature: undefined,
            bundleFeature: undefined,
            multiMarkersOrderFeature: undefined,
          },
    [fetchedBundles, fetchedOrders]
  );
  const windowGroups = useMemo(
    () =>
      !!fetchedOrders
        ? createGroupColors(uniqueWindowGroups(fetchedOrders))
        : undefined,
    [fetchedOrders]
  );

  if (activeFilters.length > 0) {
    return {
      fetchedBundles: fetchedBundles,
      fetchedOrders: fetchedOrders,
      orders: featureStructure.ordersFeature,
      bundles: featureStructure.bundleFeature,
      multiMarkers: featureStructure.multiMarkersOrderFeature,
      windowGroups: windowGroups,
      done:
        !!fetchedBundles &&
        !!fetchedOrders &&
        !!featureStructure &&
        !!featureStructure.ordersFeature &&
        !!featureStructure.bundleFeature &&
        !!featureStructure.multiMarkersOrderFeature &&
        !!windowGroups,
    };
  }

  return { done: true };
};
